import { useState } from "react";

import arrowUp from "../assets/icons/arrows/arrow-narrow-up-svgrepo-com.svg";
import arrowDown from "../assets/icons/arrows/arrow-narrow-down-svgrepo-com.svg";

function useClickFilter() {
  type SortDirection = "asc" | "desc" | "";

  // Sort state for each column
  const [projectSort, setProjectSort] = useState<SortDirection>("");
  const [customerSort, setCustomerSort] = useState<SortDirection>("");
  const [refSort, setRefSort] = useState<SortDirection>("");
  const [descriptionSort, setDescriptionSort] = useState<SortDirection>("");
  const [clientSort, setClientSort] = useState<SortDirection>("");
  const [numberSort, setNumberSort] = useState<SortDirection>("");
  const [requesterSort, setRequesterSort] = useState<SortDirection>("");
  const [coordinatorSort, setCoordinatorSort] = useState<SortDirection>("");
  const [dateSort, setDateSort] = useState<SortDirection>("");
  const [amountSort, setAmountSort] = useState<SortDirection>("");
  const [vendorSort, setVendorSort] = useState<SortDirection>("");


  // Reset all other sort states except the one being used
  const resetOtherSorts = (currentSortSetter: Function) => {
    if (currentSortSetter !== setProjectSort) setProjectSort("");
    if (currentSortSetter !== setCustomerSort) setCustomerSort("");
    if (currentSortSetter !== setRefSort) setRefSort("");
    if (currentSortSetter !== setDescriptionSort) setDescriptionSort("");
    if (currentSortSetter !== setClientSort) setClientSort("");
    if (currentSortSetter !== setNumberSort) setNumberSort("");
    if (currentSortSetter !== setRequesterSort) setRequesterSort("");
    if (currentSortSetter !== setCoordinatorSort) setCoordinatorSort("");
    if (currentSortSetter !== setDateSort) setDateSort("");
    if (currentSortSetter !== setAmountSort) setAmountSort("");
    if (currentSortSetter !== setVendorSort) setVendorSort("");
  };

  // Generic toggle function for any sort column
  const createToggleSort = (
    sortSetter: React.Dispatch<React.SetStateAction<SortDirection>>,
  ) => {
    return () => {
      sortSetter((prev) =>
        prev === "" ? "asc" : prev === "asc" ? "desc" : "",
      );
      resetOtherSorts(sortSetter);
    };
  };

  // Toggle functions for each column
  const toggleProjectSort = createToggleSort(setProjectSort);
  const toggleCustomerSort = createToggleSort(setCustomerSort);
  const toggleRefSort = createToggleSort(setRefSort);
  const toggleDescriptionSort = createToggleSort(setDescriptionSort);
  const toggleClientSort = createToggleSort(setClientSort);
  const toggleNumberSort = createToggleSort(setNumberSort);
  const toggleRequesterSort = createToggleSort(setRequesterSort);
  const toggleCoordinatorSort = createToggleSort(setCoordinatorSort);
  const toggleDateSort = createToggleSort(setDateSort);
  const toggleAmountSort = createToggleSort(setAmountSort);
  const toggleVendorSort = createToggleSort(setVendorSort);

  // Helper to get arrow icon based on sort direction
  const getArrow = (direction: SortDirection) => {
    if (direction === "asc") return arrowUp;
    if (direction === "desc") return arrowDown;
    return null;
  };

  // Sorting logic for different data types
  const sortString = (a: string, b: string, direction: SortDirection) => {
    const aValue = a ?? "";
    const bValue = b ?? "";
    return direction === "asc"
      ? aValue.localeCompare(bValue)
      : bValue.localeCompare(aValue);
  };

  const sortNumber = (a: number, b: number, direction: SortDirection) => {
    const aValue = a ?? 0;
    const bValue = b ?? 0;
    return direction === "asc" ? aValue - bValue : bValue - aValue;
  };

  const sortDate = (a: string, b: string, direction: SortDirection) => {
    const parseDate = (dateStr: string): number => {
      if (!dateStr) return 0;
      const [month, year] = dateStr.split("-").map(Number);
      return new Date(year, month - 1).getTime();
    };
    const aDate = a ? parseDate(a) : 0;
    const bDate = b ? parseDate(b) : 0;
    return direction === "asc" ? aDate - bDate : bDate - aDate;
  };

  // Main sorting function
  const getFilteredData = (dataToSort: any[]) => {
    let filteredData = [...dataToSort];

    // Apply sorting based on active sort state
    if (projectSort) {
      filteredData.sort((a, b) =>
        sortString(a.project, b.project, projectSort),
      );
    } else if (customerSort) {
      filteredData.sort((a, b) =>
        sortString(a.customer || a.name, b.customer || b.name, customerSort),
      );
    } else if (refSort) {
      filteredData.sort((a, b) => sortString(a.refrence, b.refrence, refSort));
    } else if (descriptionSort) {
      filteredData.sort((a, b) =>
        sortString(a.description, b.description, descriptionSort),
      );
    } else if (clientSort) {
      filteredData.sort((a, b) => sortString(a.client, b.client, clientSort));
    } else if (numberSort) {
      filteredData.sort((a, b) => sortString(a.number, b.number, numberSort));
    } else if (requesterSort) {
      filteredData.sort((a, b) =>
        sortString(a.requester, b.requester, requesterSort),
      );
    } else if (coordinatorSort) {
      filteredData.sort((a, b) =>
        sortString(a.coordinator, b.coordinator, coordinatorSort),
      );
    }  else if (amountSort) {
      filteredData.sort((a, b) =>
        sortNumber(a.amount, b.amount, amountSort),
      );
    } else if (vendorSort) {
      filteredData.sort((a, b) =>
        sortString(a.vendor, b.vendor, vendorSort),
      );
    } else if (dateSort) {
      filteredData.sort((a, b) => sortDate(a.period, b.period, dateSort));
    }

    return filteredData;
  };

  // Return all the functions and states
  return {
    // Sort states
    projectSort,
    customerSort,
    refSort,
    descriptionSort,
    clientSort,
    numberSort,
    requesterSort,
    coordinatorSort,
    dateSort,
    amountSort,
    vendorSort,

    // Toggle functions
    toggleProjectSort,
    toggleCustomerSort,
    toggleRefSort,
    toggleDescriptionSort,
    toggleClientSort,
    toggleNumberSort,
    toggleRequesterSort,
    toggleCoordinatorSort,
    toggleDateSort,
    toggleAmountSort,
    toggleVendorSort,

    // Helper functions
    getArrow,
    getFilteredData,
  };
}

export { useClickFilter };
